import axios from 'axios';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faEye,
  faEyeSlash,
  faBuilding,
  faCircleCheck,
  faShieldHalved,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { ownerInvitationService } from '@/services/owner-invitation.service';
import type { OwnerInvitationDescription } from '@/services/owner-invitation.service';
import { ROUTES } from '@/routes/routes';

/**
 * Phase 12.3F (G-6) — company owner activation.
 *
 * Where a newly invited company owner sets their first password. Without this
 * page the invitation flow has no landing point and a provisioned company can
 * never be signed into.
 *
 * ── Two halves, two channels ───────────────────────────────────────────────
 * The link carries `?invitation=<uuid>` — a NON-SECRET handle that says which
 * invitation is being accepted. That is what makes acceptance deterministic when
 * two companies legitimately share one owner email address (D-099/D-100): the
 * server never resolves the account from the address.
 *
 * The CODE is not in the link. It is in the body of the email and must be
 * pasted, so a forwarded link, a referrer header or a browser history entry
 * carries nothing usable on its own — the rule migration `20260815000004`
 * established for the customer portal, applied here for a far more privileged
 * account.
 *
 * ── What is deliberately NOT persisted ─────────────────────────────────────
 * The code and both password fields live in component state and nowhere else.
 * Nothing is written to localStorage or sessionStorage, nothing is added to the
 * URL, and nothing is logged. Unmounting the page destroys all of it.
 */

/**
 * Mirrors the server's password policy exactly (`validatePasswordPolicy`). Kept
 * in step deliberately: a weaker frontend rule lets the user submit something
 * the server will reject, a stricter one rejects passwords the server would
 * accept. The server stays the authority and its violations are shown verbatim.
 */
const PASSWORD_RULES: { label: string; test: (v: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { label: 'One uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'One lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'One number', test: (v) => /[0-9]/.test(v) },
  { label: 'One special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (!err.response) {
      return 'The server could not be reached. Check your connection and try again.';
    }
    const body = err.response.data as
      { error?: { code?: string; message?: string; violations?: string[] } } | undefined;
    if (body?.error?.violations?.length) return body.error.violations.join('. ');
    if (body?.error?.message) return body.error.message;
  }
  return 'Something went wrong. Please try again.';
}

export function CompanyOwnerActivationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const invitationId = params.get('invitation') ?? '';

  const [checking, setChecking] = useState(true);
  const [invitation, setInvitation] = useState<OwnerInvitationDescription | null>(null);

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!invitationId) {
      setInvitation({ valid: false, companyName: null, maskedEmail: null, expiresAt: null });
      setChecking(false);
      return;
    }
    ownerInvitationService
      .describe(invitationId)
      // A failed check is treated as "not valid" rather than as a page error:
      // the authoritative decision is made by the accept call anyway.
      .catch(() => ({ valid: false, companyName: null, maskedEmail: null, expiresAt: null }))
      .then((result) => setInvitation(result))
      .finally(() => setChecking(false));
  }, [invitationId]);

  const rulesPassed = PASSWORD_RULES.every((r) => r.test(password));
  const matches = password.length > 0 && password === confirm;
  const canSubmit = code.trim().length > 0 && rulesPassed && matches && !submitting;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      await ownerInvitationService.accept({
        invitationId,
        token: code.trim(),
        newPassword: password,
        confirmPassword: confirm,
      });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Checking ──────────────────────────────────────────────────────────────
  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  // ── Invalid / expired ─────────────────────────────────────────────────────
  if (!invitation?.valid && !done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center">
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-8 w-8 text-amber-600" />
          <h1 className="mt-3 text-lg font-semibold text-foreground">
            This invitation is no longer valid
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            It may have expired, or it may already have been used. Ask your service provider to send
            a new invitation — a new one always replaces the old.
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            className="mt-5 min-h-11 w-full rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0"
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
        <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center">
          <FontAwesomeIcon icon={faCircleCheck} className="h-9 w-9 text-green-600" />
          <h1 className="mt-3 text-lg font-semibold text-foreground">Your password is set</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You can now sign in to {invitation?.companyName ?? 'your company'} with your email
            address and the password you just chose.
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.LOGIN)}
            className="mt-5 min-h-11 w-full rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:min-h-0"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-5">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-muted">
            <FontAwesomeIcon icon={faBuilding} className="h-5 w-5 text-muted-foreground" />
          </div>
          <h1 className="mt-3 text-xl font-semibold text-foreground">
            Set up {invitation?.companyName ?? 'your company'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            You have been set up as the owner. Choose your password to finish.
          </p>
          {invitation?.maskedEmail && (
            <p className="mt-1 text-xs text-muted-foreground">
              Invitation sent to {invitation.maskedEmail}
            </p>
          )}
        </div>

        <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div>
            <label htmlFor="code" className="mb-1.5 block text-sm font-medium text-foreground">
              Invitation code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              placeholder="Paste the code from your invitation email"
              className="h-11 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm text-foreground placeholder:font-sans placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The code is in the email, not in this link — copy it across.
            </p>
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-foreground">
              New password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="h-11 w-full rounded-lg border border-border bg-background px-3 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-3.5 w-3.5" />
              </button>
            </div>

            <ul className="mt-2 space-y-1">
              {PASSWORD_RULES.map((rule) => {
                const ok = rule.test(password);
                return (
                  <li key={rule.label} className="flex items-center gap-2 text-xs">
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={ok ? 'h-3 w-3 text-green-600' : 'h-3 w-3 text-muted-foreground/40'}
                    />
                    <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>
                      {rule.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <label htmlFor="confirm" className="mb-1.5 block text-sm font-medium text-foreground">
              Confirm password
            </label>
            <input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {confirm.length > 0 && !matches && (
              <p className="mt-1 text-xs text-destructive">Passwords do not match.</p>
            )}
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
            )}
            {submitting ? 'Setting your password…' : 'Set password'}
          </button>
        </form>

        <p className="flex items-start justify-center gap-2 text-center text-xs text-muted-foreground">
          <FontAwesomeIcon icon={faShieldHalved} className="mt-0.5 h-3 w-3 shrink-0" />
          Nobody else has a password for this account — not even your service provider.
        </p>
      </div>
    </div>
  );
}
